import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    Renderer2
} from '@angular/core';
import {SafeHtml} from '@angular/platform-browser';
import {isFinite} from 'lodash-es';
import {createNumberFormat} from '../../common/formatters';
import {TypedSimpleChanges} from '../../common/typed-on-changes';
import {HtmlSymbolService} from '../../services/html-symbol.service';
import {
    addCurrencyToDisplayDecimal,
    createDisplayDecimal,
    DisplayDecimal,
    DisplayDecimalWithOptCurrency,
    formatDisplayDecimalAsString,
    ValueFormatterWithOptCurrency
} from '../symbol-decimals';
import {TooltipDirective} from '../tooltip/tooltip.directive';

const ZERO_CLASS = '-zero'; // Allows to apply styles to '0' (without decimals).

const ZERO_DISPLAY_DECIMAL: DisplayDecimal = {
    value:         '0',
    class:         ZERO_CLASS,
    originalValue: 0
};

const EMPTY_FORMATTER: ValueFormatterWithOptCurrency = () => undefined;

@Component({
    selector: '[displayDecimal]',
    templateUrl: './display-decimal.component.html',
    styleUrls: ['./display-decimal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: true,
    standalone: false
})
export class DisplayDecimalComponent implements OnChanges {

    private static isDisplayDecimal(value: number | DisplayDecimal): value is DisplayDecimal {
        return !!value && (value as DisplayDecimal).hasOwnProperty('value');
    }

    @Input('displayDecimal')
    public value: number | DisplayDecimalWithOptCurrency;

    @Input()
    public formatter: (value: number) => DisplayDecimalWithOptCurrency;

    @Input()
    public decimalCount: number;

    @Input()
    public precision: number;

    @Input()
    public isDecimalRequired: boolean = true;

    @Input()
    public isZeroWithoutDecimals: boolean = false;

    @Input()
    public currency: string;

    @Input()
    public dashIfEmpty: boolean = false;

    @Input()
    public noTrailingDecimalZeroes: boolean = false;

    @Input()
    public tooltipDirective: TooltipDirective;

    @Input()
    public overrideCurrency: boolean = false;

    public displayValue: DisplayDecimalWithOptCurrency;

    private element: HTMLElement = this.elementRef.nativeElement;

    private internalFormatter: (value: number) => DisplayDecimalWithOptCurrency;

    constructor(
        private elementRef: ElementRef,
        private changeDetectorRef: ChangeDetectorRef,
        private renderer: Renderer2,
        private htmlSymbolService: HtmlSymbolService
    ) {}

    // tslint:disable:cyclomatic-complexity
    public ngOnChanges(changes: TypedSimpleChanges<DisplayDecimalComponent>): void {
        let isInternalFormatterChanged: boolean = false;

        if (
            !this.internalFormatter
            || changes.formatter || changes.decimalCount || changes.precision || changes.isDecimalRequired
            || changes.currency || changes.overrideCurrency
            || changes.isZeroWithoutDecimals
        ) {
            this.updateInternalFormatter();
            isInternalFormatterChanged = true;
        }

        if (changes.value || isInternalFormatterChanged || changes.noTrailingDecimalZeroes) {
            this.updateDisplayValue();
        } else if (changes.tooltipDirective) {
            this.updateTooltip();
        }
    }

    // tslint:disable:cyclomatic-complexity
    private updateDisplayValue(): void {
        let displayValue = DisplayDecimalComponent.isDisplayDecimal(this.value)
            ? this.overrideCurrency
                ? addCurrencyToDisplayDecimal({...this.value}, this.currency)
                : this.value
            : this.internalFormatter(this.value);

        if (this.dashIfEmpty) {
            displayValue = this.dashDisplayDecimalIfEmpty(displayValue);
        }

        const oldClass = !this.noTrailingDecimalZeroes
            && this.displayValue && this.displayValue.class
            || '';
        const newClass = !this.noTrailingDecimalZeroes
            && displayValue && displayValue.class
            || '';

        if (newClass !== oldClass) {
            if (oldClass) {
                this.renderer.removeClass(this.element, oldClass);
            }

            if (newClass) {
                this.renderer.addClass(this.element, newClass);
            }
        }

        this.displayValue = displayValue;
        this.updateTooltip();
        this.changeDetectorRef.detectChanges();
    }

    private getEmptyDisplayDecimalAsDash(): DisplayDecimalWithOptCurrency<SafeHtml> {
        return {
            value: this.htmlSymbolService.mdash(),
            class: ''
        };
    }

    private dashDisplayDecimalIfEmpty(displayDecimal: DisplayDecimalWithOptCurrency): DisplayDecimalWithOptCurrency {
        return displayDecimal && displayDecimal.value
            ? displayDecimal
            : this.getEmptyDisplayDecimalAsDash();
    }

    private updateTooltip(): void {
        if (this.tooltipDirective) {
            this.tooltipDirective.setDynamicTooltip(true, formatDisplayDecimalAsString(this.displayValue));
        }
    }

    private updateInternalFormatter(): void {
        let formatter: ValueFormatterWithOptCurrency;

        if (this.formatter) {
            formatter = this.overrideCurrency
                ? value =>
                    addCurrencyToDisplayDecimal(
                        this.patchFormatterForZero(this.formatter, false)(value),
                        this.currency
                    )
                : this.patchFormatterForZero(this.formatter, true);
        } else if (isFinite(this.decimalCount) || isFinite(this.precision)) {
            const numberFormat = createNumberFormat({
                decimalCount:      this.decimalCount,
                precision:         this.precision,
                isDecimalRequired: this.isDecimalRequired
            });

            const zeroHandledFormatter = this.patchFormatterForZero(
                value => createDisplayDecimal(value, numberFormat),
                false
            );

            formatter = this.currency
                ? value => addCurrencyToDisplayDecimal(zeroHandledFormatter(value), this.currency)
                : zeroHandledFormatter;
        } else {
            formatter = EMPTY_FORMATTER;
        }

        this.internalFormatter = formatter;
    }

    private patchFormatterForZero(
        formatter: ValueFormatterWithOptCurrency,
        keepCurrency: boolean
    ): ValueFormatterWithOptCurrency {
        return this.isZeroWithoutDecimals
            ? keepCurrency
                ? value => value === 0 ? ZERO_DISPLAY_DECIMAL : formatter(value)
                : value => {
                    const result = formatter(value);

                    if (result.value === 0) {
                        result.class = ZERO_CLASS;
                    }

                    return result;
                }
            : formatter;
    }
}
